import React, { useContext } from "react";
import SimpleCollapsible from "./SimpleCollapsible";
import ThemeSelector from "./Display/ThemeSelector";
import ThresholdSelector from "./Display/ThresholdSelector";
import ReferenceAreaSelector from "./Display/ReferenceAreaSelector";
import AxisSelector from "./Display/AxisSelector";
import LegendConfig from "./Display/LegendConfig";
import PieLabelConfig from "./Display/PieLabelConfig";
import SwitchTitleDescription from "./common/SwitchTitleDescription";
import GraphConfigFormContext from "@/store/contexts/GraphConfigFormContext";
import { useTranslation } from "react-i18next";

const Display = () => {
  const { config, setConfig } = useContext(GraphConfigFormContext);
  const { t } = useTranslation();

  if (!config) return null;

  return (
    <div className="flex flex-col w-full px-4 pt-4 pb-[100px]">
      <div className="flex flex-col w-full gap-4">
        <ThemeSelector />
        <SwitchTitleDescription
          title={t("editor.graphConfig.display.stacked")}
          description={t("editor.graphConfig.display.stackedDescription")}
          value={config.graph_options.stacked}
          onChange={(checked: boolean) =>
            setConfig({
              ...config,
              graph_options: {
                ...config.graph_options,
                stacked: checked,
                barOverlay: checked ? false : config.graph_options.barOverlay,
              },
            })
          }
        />
        {config.graph_options.chartType === "bar" && (
          <SwitchTitleDescription
            title={t("editor.graphConfig.display.overlayBars")}
            description={t("editor.graphConfig.display.overlayBarsDescription")}
            value={config.graph_options.barOverlay}
            onChange={(checked: boolean) =>
              setConfig({
                ...config,
                graph_options: {
                  ...config.graph_options,
                  barOverlay: checked,
                  stacked: checked ? false : config.graph_options.stacked,
                },
              })
            }
          />
        )}
        <SwitchTitleDescription
          title={t("editor.graphConfig.display.animate")}
          description={t("editor.graphConfig.display.animateDescription")}
          value={config.graph_options.animation}
          onChange={(checked: boolean) =>
            setConfig({
              ...config,
              graph_options: {
                ...config.graph_options,
                animation: checked,
              },
            })
          }
        />
        <SwitchTitleDescription
          title={t("editor.graphConfig.display.showGrid")}
          description={t("editor.graphConfig.display.showGridDescription")}
          value={config.graph_options.showGrid}
          onChange={(checked: boolean) =>
            setConfig({
              ...config,
              graph_options: {
                ...config.graph_options,
                showGrid: checked,
              },
            })
          }
        />
        <SwitchTitleDescription
          title={t("editor.graphConfig.display.showRecordCount")}
          description={t("editor.graphConfig.display.showRecordCountDescription")}
          value={config.graph_options.showRecordCount ?? true}
          onChange={(checked: boolean) =>
            setConfig({
              ...config,
              graph_options: {
                ...config.graph_options,
                showRecordCount: checked,
              },
            })
          }
        />
      </div>

      <SimpleCollapsible title={t("editor.graphConfig.display.sections.legendConfiguration")} defaultOpen={false}>
        <LegendConfig />
      </SimpleCollapsible>

      {(config.graph_options.chartType === "pie" || config.graph_options.chartType === "donut") && (
        <SimpleCollapsible title="Pie/Donut Labels" defaultOpen={false}>
          <PieLabelConfig />
        </SimpleCollapsible>
      )}

      <SimpleCollapsible title={t("editor.graphConfig.display.sections.axesConfiguration")} defaultOpen={false}>
        <AxisSelector />
      </SimpleCollapsible>

      <SimpleCollapsible title={t("editor.graphConfig.display.sections.thresholdLines")} defaultOpen={false}>
        <ThresholdSelector />
      </SimpleCollapsible>

      <SimpleCollapsible title={t("editor.graphConfig.display.sections.referenceAreas")} defaultOpen={false}>
        <ReferenceAreaSelector />
      </SimpleCollapsible>
    </div>
  );
};

export default Display;
